import { BaseType, select as d3Select } from "d3";
import { produce } from "immer";
import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";

import { Cartesian } from "@examspark/types";

import { calculateScales } from "./d3Utils";
import { useGraphDecorator } from "./decorators/drawGraph";
import { useListenPointsDecorator } from "./decorators/drawListenPoints";
import { useMarginLabels } from "./decorators/drawMarginLabels";
import { useShapesDecorator } from "./decorators/drawShapes";

export type WithIndex<T> = T & { index: number };

const Styled = styled.div`
  user-select: none;
  display: flex;
  justify-content: center;
  svg {
    display: block;
  }
`;

const Wrapper = styled.div`
  background-color: #fff;
  border: solid 1px #ddd;
`;

export interface CartesianGraphProps extends Cartesian.Graph {
  mode?: string;
  onChange?: (shapes: Cartesian.Shape[]) => void;
}

const CartesianGraph: React.FC<React.PropsWithChildren<CartesianGraphProps>> = (
  props,
) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const extra_margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };
  if (props.marginLabelTop || props.y.label) {
    extra_margin.top = 20;
  }

  // we need to make room for long x labels
  if (props.marginLabelRight || props.x.label) {
    extra_margin.right = Math.max(20, 5 * (props.x.label?.length ?? 0));
  }
  if (props.marginLabelBottom) {
    extra_margin.bottom = 20;
  }
  // and for long y tick numbers
  let yTickNumberWidth =
    5.5 * (props.y.max.toString().length + (props.y?.precision ?? 0)) - 10;

  if (props.marginLabelLeft) {
    yTickNumberWidth += 20;
  }

  extra_margin.left = Math.max(0, yTickNumberWidth);

  // const margin = { top: 0, right: 0, bottom: 0, left: 0 };
  const margin = {
    top: 20 + extra_margin.top,
    right: 20 + extra_margin.right,
    bottom: 20 + extra_margin.bottom,
    left: 30 + extra_margin.left,
  };
  const width = props.width - margin.left - margin.right;
  const height = props.height - margin.top - margin.bottom;

  const { x, y } = calculateScales({ width, height, x: props.x, y: props.y });

  const decorateGraph = useGraphDecorator<SVGSVGElement>({
    x,
    y,
    margin,
  });

  const decorateShapes = useShapesDecorator<BaseType | SVGGElement>({
    x,
    y,
    onErase: (shapeIndex) => {
      if (props.mode !== "erase") return;

      const newShapes = [...props.shapes];
      newShapes.splice(shapeIndex, 1);

      props.onChange?.(newShapes);
    },
    onMove: (shapeIndex, coordIndex, coord) => {
      if (props.mode !== undefined) return;

      props.onChange?.(
        produce(props.shapes, (draft) => {
          const shape = draft[shapeIndex];
          if ("coords" in shape) {
            shape.coords[coordIndex] = coord;
          } else {
            shape.coord = coord;
          }

          return draft;
        }),
      );
    },
  });

  const decorateListenPoints = useListenPointsDecorator({
    x,
    y,
    mode: props.mode,
    shapes: props.shapes,
    onChange: props.onChange,
  });

  const decorateMarginLabels = useMarginLabels({
    width,
    height,
    props,
    margin,
  });

  useLayoutEffect(() => {
    if (!svgRef.current) return;

    const canvas = d3Select(svgRef.current);

    const graph = canvas.call(decorateGraph).select(".graph");

    graph.call(decorateMarginLabels);

    const shapes_container = graph
      .selectAll("g.shapes")
      .data([null])
      .join("g")
      .attr("class", "shapes");

    // const shapes =
    shapes_container
      .selectAll("g.shape")
      .data(props.shapes.map((shape, index) => ({ ...shape, index })))
      .join("g")
      .attr("class", "shape")
      .call(decorateShapes);

    graph.call(decorateListenPoints);
  });

  return (
    <Styled>
      <Wrapper>
        <svg
          ref={svgRef}
          width={props.width}
          height={props.height}
          style={{ shapeRendering: "geometricPrecision" }}
        >
          <defs>
            <marker
              id="arrow"
              markerWidth="13"
              markerHeight="13"
              refX="1.5"
              refY="4.5"
              style={{ fill: "#1fa4e1" }}
              orient="auto-start-reverse"
            >
              <path d="M 1.5 1.5 L 1.5 8.25 L 7.5 4.5 L 1.5 1.5" />
            </marker>
            <marker
              id="arrow-with-tail"
              markerWidth="13"
              markerHeight="13"
              refX="3"
              refY="4.5"
              style={{ fill: "black" }}
              orient="auto-start-reverse"
            >
              <path d="M 6 1.5 L 6 4 L 3 4 L 3 5 L 6 5 L 6 7.5 L 12 4.5 L 6 1.5" />
            </marker>
          </defs>
        </svg>
      </Wrapper>
    </Styled>
  );
};

export default CartesianGraph;
